import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IconArrowRight, InlineTextButton, NamedLink } from '../../components';

import css from './TabNavHorizontal.module.css';

export const LIGHT_SKIN = 'light';
export const DARK_SKIN = 'dark';

const { arrayOf, bool, func, node, object, oneOf, string, shape } = PropTypes;

const Tab = props => {
  const {
    className,
    disabled,
    text,
    selected,
    onClick,
    linkProps,
    isDark,
    buttonClassName,
    selectedClassName,
  } = props;
  const darkSkinClasses = isDark
    ? classNames(css.tabContentDarkSkin, {
        [css.selectedTabContentDarkSkin]: selected,
        [css.disabledDarkSkin]: disabled,
      })
    : null;

  const linkClasses = classNames(
    css.tabContent,
    {
      [css.selectedTabContent]: selected,
      [css.disabled]: disabled,
      [selectedClassName]: selected,
    },
    darkSkinClasses,
    buttonClassName
  );

  const buttonClasses = classNames(
    css.tabContent,
    css.button,
    {
      [css.selectedTabContent]: selected,
      [css.disabled]: disabled,
      [selectedClassName]: selected,
    },
    darkSkinClasses,
    buttonClassName
  );

  const isButton = !!onClick;
  return (
    <div className={className}>
      {isButton ? (
        <InlineTextButton rootClassName={buttonClasses} onClick={onClick}>
          {text}
        </InlineTextButton>
      ) : (
        <NamedLink className={linkClasses} {...linkProps}>
          {text}
        </NamedLink>
      )}
    </div>
  );
};

Tab.defaultProps = { className: null, disabled: false, selected: false };

Tab.propTypes = {
  className: string,
  text: node.isRequired,
  disabled: bool,
  selected: bool,
  onClick: func,
  linkProps: object,
  isDark: bool.isRequired,
};

const SCROLL_AMMOUNT = 200;

const TabNavHorizontal = props => {
  const { className, rootClassName, tabRootClassName, tabClassName, tabs, skin, arrows } = props;
  const navRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateScrollVisibility = () => {
    if (!navRef.current) return;

    const { scrollWidth, scrollLeft, clientWidth } = navRef.current;

    setIsScrollable(scrollWidth > clientWidth);
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollWidth - clientWidth > scrollLeft);
  };

  const scroll = direction => {
    if (navRef.current) {
      navRef.current.scrollBy({
        left: direction === 'left' ? -SCROLL_AMMOUNT : SCROLL_AMMOUNT,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (navRef.current) {
      updateScrollVisibility();
      navRef.current.addEventListener('scroll', updateScrollVisibility);
      window.addEventListener('resize', updateScrollVisibility);
    }
    return () => {
      navRef.current?.removeEventListener('scroll', updateScrollVisibility);
      window.removeEventListener('resize', updateScrollVisibility);
    };
  }, []);

  const isDark = skin === DARK_SKIN;

  const classes = classNames(rootClassName || css.root, { [css.darkSkin]: isDark }, className);
  const tabClasses = classNames(tabRootClassName || css.tab, tabClassName);
  return (
    <div className={classNames(css.container, { [css.arrows]: arrows })}>
      {canScrollLeft && isScrollable && (
        <button className={classNames(css.arrow, css.arrowLeft)} onClick={() => scroll('left')}>
          <IconArrowRight />
        </button>
      )}
      {canScrollRight && isScrollable && (
        <button className={classNames(css.arrow, css.arrowRight)} onClick={() => scroll()}>
          <IconArrowRight />
        </button>
      )}
      <nav className={classes} ref={navRef}>
        {tabs.map((tab, index) => {
          const key = typeof tab.text === 'string' ? tab.text : index;
          return <Tab key={key} className={tabClasses} {...tab} isDark={isDark} />;
        })}
      </nav>
    </div>
  );
};

/**
 * A tab navigation element with buttons. Requires onClick
 * function param for tab objects passed as parameter.
 */
export const ButtonTabNavHorizontal = props => <TabNavHorizontal {...props} />;

ButtonTabNavHorizontal.defaultProps = {
  className: null,
  rootClassName: null,
  tabRootClassName: null,
  tabClassName: null,
  skin: LIGHT_SKIN,
};

ButtonTabNavHorizontal.propTypes = {
  className: string,
  rootClassName: string,
  tabRootClassName: string,
  tabs: arrayOf(
    shape({
      text: node.isRequired,
      disabled: bool,
      selected: bool,
      onClick: func.isRequired,
    })
  ).isRequired,
  skin: oneOf([LIGHT_SKIN, DARK_SKIN]),
};

/**
 * A tab navigation element with links. Requires linkProps
 * object param for tab objects passed as parameter.
 */
export const LinkTabNavHorizontal = props => <TabNavHorizontal {...props} />;

LinkTabNavHorizontal.defaultProps = {
  className: null,
  rootClassName: null,
  tabRootClassName: null,
  tabClassName: null,
  skin: LIGHT_SKIN,
};

LinkTabNavHorizontal.propTypes = {
  className: string,
  rootClassName: string,
  tabRootClassName: string,
  tabs: arrayOf(
    shape({
      text: node.isRequired,
      disabled: bool,
      selected: bool,
      linkProps: object.isRequired,
    })
  ).isRequired,
  skin: oneOf([LIGHT_SKIN, DARK_SKIN]),
};
